/* global google Notification window */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import css from '../../Login.scss';
import { FullPageLoader, Overlay } from 'panamera-react-ui';
import withConfig from 'HOCs/withConfig/withConfig';
import withTrack from 'HOCs/withTrack/withTrack';

import CookieManager from 'Helpers/cookies';
import { getError } from 'Helpers/errors';
import { parserToken } from 'Helpers/authentication';

import TermsAndConditions from '../../steps/TermsAndConditions';
import { LoginTrackingKeys } from 'Constants/tracking';
import NotificationPermission from '../../steps/NotificationPermission';
import Auth from '../../../../Auth';
import { configSelector, getLocaleZendeskUrl } from 'Selectors/config';
import setLoginModalState from 'Actions/setLoginModalState';
import { setNotificationToken, askForPermissionToReceiveNotifications, initPlush } from 'Helpers/pushNotification';
import { authenticateLogin, getUserConsents } from 'Actions/user';
import { getNotifications } from 'Actions/notifications';
import { preNotificationAvailable } from 'Actions/preNotification';
import { withExperiment } from 'LaquesisEOL/withExperiment';
import experiment from './GoogleYoloExperiment.APP_TARGET';
import UserSuspended from 'Components/Login/components/UserSuspended/UserSuspended';
import { noop } from 'Helpers/function';
import { CONSENT_TYPE } from 'Constants/users';
import { userConsentsScreenShow } from 'Helpers/userConsents';

export class GoogleYolo extends React.Component {
    static propTypes = {
        config: PropTypes.shape({
            get: PropTypes.func
        }),
        _config: PropTypes.object,
        track: PropTypes.func.isRequired,
        _setLoginModalState: PropTypes.func,
        authenticateLogin: PropTypes.func.isRequired,
        getNotifications: PropTypes.func.isRequired,
        actionPreNotificationAvailable: PropTypes.func,
        variant: PropTypes.string,
        zendeskUrl: PropTypes.string,
        intl: PropTypes.shape({
            formatMessage: PropTypes.func.isRequired
        }),
        getUserConsents: PropTypes.func.isRequired
    }

    static defaultProps = {
        _config: { features: {}},
        track: noop,
        authenticateLogin: noop,
        getNotifications: noop,
        config: { get: noop },
        loginMethods: []
    };

    constructor(props) {
        super(props);

        this.state = {
            showFullLoader: false,
            showGdpr: false,
            showNotificationPermission: false,
            accessToken: '',
            userBannedPopup: false
        };

        this.loginState = { state: false, redirectTo: '', trackProps: {}, entryPoint: 0 };
        this.googleClientId = props.config.get('login', 'googleClientId');
        this.loginMethods = props.config.get('loginMethods');
        const googleYoloConf = this.loginMethods.find(loginMethod => {
            return loginMethod.name === 'google_yolo';
        });

        this.isNotificationHubEnabled = props.config.get('notificationHub', 'enabled');

        this.googleYoloEnabled = googleYoloConf && googleYoloConf.enabled || false;
        this.enableConsentsInLoginFlow = props.config.get('enableConsentsInLoginFlow');
    }

    componentDidMount() {
        const findCookie = CookieManager.readCookie('g_state');

        if (findCookie) {
            CookieManager.eraseCookie('g_state');
        }

        if (this.googleYoloEnabled) {
            this.loadGoogleSDK();
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.state.showFullLoader !== nextState.showFullLoader) {
            return true;
        }

        if (this.state.showGdpr !== nextState.showGdpr) {
            return true;
        }

        if (this.state.showNotificationPermission !== nextState.showNotificationPermission) {
            return true;
        }

        if (this.state.userBannedPopup !== nextState.userBannedPopup) {
            return true;
        }

        return false;
    }

    loadGoogleSDK = () => {
        ((d, s, id, cb) => {
            const element = d.getElementsByTagName(s)[0];
            const fjs = element;
            const js = d.createElement(s);

            js.id = id;
            js.src = '//accounts.google.com/gsi/client';
            js.onload = cb;
            fjs.parentNode.insertBefore(js, fjs);
        })(document, 'script', 'google-login', () => {
            this.hint();
        });
    }

    hint = () => {
        google.accounts.id.initialize({
            client_id: this.googleClientId,
            callback: this.handleCredentialResponse
        });
        google.accounts.id.prompt(notification => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                const skippedReason = notification.getSkippedReason();

                this.checkError(skippedReason);
            }
        });
    }

    handleCredentialResponse = obj => {
        const gdprConfig = this.props._config.features.find(feature => {
            return feature.name === 'terms_gdpr';
        });

        const configConsentsData = this.props._config?.users?.consentData?.consents || [];

        if (obj.credential) {
            if (gdprConfig.enabled) {
                this.setState({
                    showGdpr: true,
                    accessToken: obj.credential
                });
            }
            else if (this.enableConsentsInLoginFlow && !!(configConsentsData.length)) {
                this.checkConsents(obj.credential);
            }
            else {
                this.loginWithGoogle(obj.credential);
            }
        }
    }

    checkConsents = tokenObj => {
        const { getUserConsents, _config } = this.props;
        const consentsConfig = _config?.users?.consentData || {};

        this.displayFullLoader(true);

        getUserConsents(tokenObj, res => {
            if (res.data && res.ok) {
                if (userConsentsScreenShow(consentsConfig?.consents || [], res.data?.data?.consents || {})) {
                    this.openConsentsModal(res, tokenObj);
                }
                else {
                    this.loginWithGoogle(tokenObj);
                }
            }
            else {
                const error = getError(res);

                if (error === 'user_banned') {
                    this.setState({ userBannedPopup: true });
                }
                this.displayFullLoader(false);

                if (userConsentsScreenShow(consentsConfig?.consents || [], res.data?.data?.consents || {})) {
                    this.openConsentsModal(res, tokenObj);
                }
                else {
                    this.loginWithGoogle(tokenObj);
                }
            }
        });
    }

    openConsentsModal = (res, token) => {
        const { _setLoginModalState } = this.props;

        const consentsRelatedProps = {
            loginMethod: 'google',
            login_submethod: 'googleYolo',
            select_from: 'google_yolo_popup',
            userConsents: res?.data?.data?.consents || {},
            isSocialLogin: true,
            socialAuth: { type: 'gplus', identifier: token }
        };

        this.track(LoginTrackingKeys.LOGIN_SIGN_IN_START, {
            login_method: 'google',
            login_submethod: 'googleYolo'
        });

        _setLoginModalState({ state: true, redirectTo: '', trackProps: { origin: 'googleYolo' }, openConsentsPageDirectly: true, consentsRelatedProps });
    }

    checkError = reason => {
        if (reason === 'user_cancel' || reason === 'tap_outside' || reason === 'auto_cancel') {
            this.track(LoginTrackingKeys.LOGIN_YOLO_CANCEL, {
                login_method: 'google',
                login_submethod: 'googleYolo',
                error_message: reason,
                error_origin: 'google_loginWithGoogle'
            });
        }
    }

    loginWithGoogle = token => {
        this.displayFullLoader(true);
        this.track(LoginTrackingKeys.LOGIN_SIGN_IN_START, {
            login_method: 'google',
            login_submethod: 'googleYolo'
        });

        const params = {
            grantType: 'gplus',
            token
        };

        const configConsentsData = this.props._config?.users?.consentData?.consents || [];
        const shouldCallV3API = this.enableConsentsInLoginFlow && !!(configConsentsData.length);

        this.props.authenticateLogin(params, res => {
            if (!res.data || !res.ok) {
                const error = getError(res);

                if (error === 'user_banned') {
                    this.setState({ userBannedPopup: true });
                }
                this.track(LoginTrackingKeys.LOGIN_ERRORS, {
                    login_method: 'google',
                    login_submethod: 'googleYolo',
                    error_message: error,
                    error_origin: 'google_loginWithGoogle'
                });
            }
            else {
                const { user: { id: userId }, access_token } = res.data;

                Auth.setChatTokenInLocalStorage(userId, () => {
                    const { is_new_user } = parserToken(access_token);

                    this.track(LoginTrackingKeys.LOGIN_SIGN_IN_COMPLETE, {
                        login_method: 'google',
                        login_submethod: 'googleYolo',
                        login_type: res.data.user && is_new_user ? 'register' : 'login'
                    });
                    this.checkNotification();
                });
            }
            this.displayFullLoader(false);
        }, shouldCallV3API);
    }

    track = (event, props, setLoginModalStateProp = false) => {
        this.props.track(event, props);
        const { _setLoginModalState } = this.props;

        if (setLoginModalStateProp) {
            _setLoginModalState(this.loginState);
        }
    }

    displayFullLoader = showFullLoader => {
        this.setState({
            showFullLoader
        });
    }

    checkNotification = async () => {
        this.displayFullLoader(false);
        await initPlush();
        const { actionPreNotificationAvailable, variant } = this.props;

        this.displayFullLoader(true);
        if (!('Notification' in window)) {
            this.onLoginOk();
            return;
        }

        if (Notification.permission === 'granted') {
            setNotificationToken().then(() => this.onLoginOk());
        }
        else if (Notification.permission === 'default') {
            this.setState({
                showNotificationPermission: true
            });
            if (variant === 'b') {
                actionPreNotificationAvailable(true);
                this.onLoginOk(false);
            }
            else {
                actionPreNotificationAvailable(false);
                askForPermissionToReceiveNotifications(this.track)
                    .then(() => this.onLoginOk(false));
            }
        }
        else {
            this.onLoginOk();
        }
    }

    onLoginOk = (setLoginModalStateProp = true) => {
        this.displayFullLoader(false);

        if (this.isNotificationHubEnabled) {
            this.props.getNotifications();
        }

        const { _setLoginModalState } = this.props;

        if (setLoginModalStateProp) {
            _setLoginModalState(this.loginState);
        }
    }

    onAccept = () => {
        if (this.state.accessToken) {
            this.displayFullLoader(true);
            this.loginWithGoogle(this.state.accessToken);
        }
    }

    onDeny = () => {
        this.setState({
            showGdpr: false,
            accessToken: ''
        });
    }

    closeNotificationPermission = () => {
        this.setState({
            showNotificationPermission: false
        });
    }

    closeUserBannedPopup = () => {
        this.setState({
            userBannedPopup: false
        });
    }

    openHelpCenter = () => {
        const { config, zendeskUrl } = this.props;
        const isMxCl = config.get('adpvAuto', 'showMXDesign');
        const url = isMxCl ? (config.get('staticAssets') + config.get('redirectionTermsAndConditions')).toString() : zendeskUrl;

        this.closeUserBannedPopup();
        window.open(url, '_blank');
    }

    render() {
        if (this.state.showFullLoader) {
            return <FullPageLoader data-aut-id="yolo-full-page-loader" />;
        }

        if (this.state.userBannedPopup) {
            return (
                <UserSuspended
                    isOpen={ this.state.userBannedPopup }
                    onAccept={ this.closeUserBannedPopup }
                    onDecline={ this.openHelpCenter }
                    data-aut-id="UserSuspended"
                />
            );
        }

        const showModal = this.state.showGdpr || this.state.showNotificationPermission;

        if (showModal) {
            return (
                <Overlay
                    isOpened={ showModal }
                    overlayClassName={ css.login }
                    contentClassName={ css.modal }
                    onClose={ this.onDeny }
                    data-aut-id="google-yolo-overlay"
                >
                    { this.state.showGdpr
                    && <TermsAndConditions
                        onClickAccept={ this.onAccept }
                        onClickDeny={ this.onDeny }
                        data-aut-id="terms-and-conditions"
                    />
                    }
                    { this.state.showNotificationPermission
                    && <NotificationPermission onClickGotIt={ this.closeNotificationPermission } />
                    }
                </Overlay>
            );
        }
        return null;
    }
}

export const mapStateToProps = (state, ownProps) => {
    return {
        _config: configSelector(state),
        variant: state.a,
        zendeskUrl: getLocaleZendeskUrl(state, ownProps)
    };
};

export const mapDispatchToProps = dispatch => ({
    getUserConsents: (socialToken, callback) => dispatch(getUserConsents(CONSENT_TYPE.GPLUS, socialToken)).then(res => callback(res)),
    authenticateLogin: (params, callback = noop, isV3) => dispatch(authenticateLogin(params, isV3)).then(res => callback(res)),
    _setLoginModalState: state => dispatch(setLoginModalState(state)),
    getNotifications: () => dispatch(getNotifications()),
    actionPreNotificationAvailable: value => dispatch(preNotificationAvailable(value))
});

export default compose(
    withConfig,
    withTrack,
    connect(mapStateToProps, mapDispatchToProps),
    withExperiment(experiment)
)(GoogleYolo);
